import React from "react";
import {
  ScrollView,
  View,
  Text,
  ActivityIndicator,
  useWindowDimensions,
} from "react-native";
import { Tab2, TitleText } from "../../../common/native";
import MessageInbox from "./MessageInbox";
import MessageItem from "./MessageItem";
import { SessionContext } from "../../../../contexts";
import {
  TGetConversationsResponse,
  TGetMessagesResponse,
  getConversations,
  getLastMessage,
  getMessages,
  patchConversations,
} from "../../../../services/api-clinic";
import MessageThread from "./MessageThread";

const HomeMessages: React.FC = () => {
  const session = React.useContext(SessionContext)?.session;
  const { width, height } = useWindowDimensions();
  const isMobile = Math.min(width, height) < 644;

  const [conversations, setConversations] =
    React.useState<TGetConversationsResponse>([]);
  const [selectedConversations, setSelectedConversations] = React.useState<
    number | null
  >(null);
  const [numberOfNewMessages, setNumberOfNewMessages] = React.useState<
    number | null
  >(null);
  const [showUnreadMessagesOnly, setShowUnreadMessagesOnly] =
    React.useState<boolean>(false);
  const [messageThread, setMessageThread] =
    React.useState<TGetMessagesResponse | null>(null);
  const [selectedTab, setSelectedTab] = React.useState<
    "All" | "Leads" | "Patients"
  >("All");
  const [loading, setLoading] = React.useState<boolean>(false);
  const lastMessageIdRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    const fetchMessages = async () => {
      try {
        if (!session) return;
        setLoading(true);
        const response = await getConversations(session);
        setConversations(response);

        // Count the number of unread messages (clinic_read: false)
        const unreadMessagesCount = response.filter(
          (conversation) => !conversation.clinic_read,
        ).length;

        // Set the number of new messages state
        setNumberOfNewMessages(unreadMessagesCount);
      } catch (error) {
        console.error("Error fetching messages:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, []);

  const fetchLatestMessageThread = async (
    patientUuid: string,
    conversationId: number,
  ) => {
    try {
      if (!session) return;

      const body = {
        patient_uuid: patientUuid,
        conversation_id: conversationId,
      };
      const getLastMessageResponse = await getLastMessage(session, body);

      // Check if the last_message_id has changed
      if (getLastMessageResponse.last_message_id !== lastMessageIdRef.current) {
        lastMessageIdRef.current = getLastMessageResponse.last_message_id;

        const query = {
          get_patient_info: false,
          patient_uuid: patientUuid,
        };
        const response = await getMessages(session, query);
        setMessageThread((prev) =>
          prev ? { ...prev, messages: response.messages } : null,
        );
      }
    } catch (error) {
      console.error("Error fetching latest message thread:", error);
    }
  };

  const updateConversationToRead = async (
    conversation: { patient_uuid: string },
    index: number,
  ) => {
    if (!session) return;

    try {
      // Check if the conversation is unread before updating
      const isUnread = !conversations[index].clinic_read;

      // Update the conversation to read
      setConversations((prev) => {
        const newConversations = [...prev];
        newConversations[index] = {
          ...newConversations[index],
          clinic_read: true,
        };
        return newConversations;
      });

      // Make the API call to update the server
      const query = { patient_uuid: conversation.patient_uuid };
      const body = { clinic_read: true };
      await patchConversations(session, query, body);

      // If the message was unread, decrement the number of new messages
      if (isUnread) {
        setNumberOfNewMessages((prevCount) =>
          Math.max(0, (prevCount ?? 1) - 1),
        );
      }
    } catch (error) {
      console.log("Error updating conversation to read:", error);
    }
  };

  const fetchMessages = async (patient_uuid: string) => {
    try {
      if (!session) return;
      const query = {
        get_patient_info: true,
        patient_uuid: patient_uuid,
      };
      const response = await getMessages(session, query);
      setMessageThread(response);
    } catch (error) {}
  };

  React.useEffect(() => {
    const intervalId = setInterval(() => {
      if (selectedConversations !== null) {
        const patientUuid = conversations[selectedConversations]?.patient_uuid;
        if (patientUuid) {
          fetchLatestMessageThread(
            patientUuid,
            conversations[selectedConversations].id,
          );
        }
      }
    }, 5000);

    // Cleanup the interval when the component unmounts or selectedConversations changes
    return () => clearInterval(intervalId);
  }, [selectedConversations, conversations]);

  const filteredConversations = showUnreadMessagesOnly
    ? conversations.filter(
        (conversation) =>
          !conversation.clinic_read &&
          (selectedTab === "All" ||
            (selectedTab === "Leads" &&
              conversation.conversation_type === "LEAD") ||
            (selectedTab === "Patients" &&
              conversation.conversation_type === "PATIENT")),
      )
    : conversations.filter(
        (conversation) =>
          selectedTab === "All" ||
          (selectedTab === "Leads" &&
            conversation.conversation_type === "LEAD") ||
          (selectedTab === "Patients" &&
            conversation.conversation_type === "PATIENT"),
      );

  return (
    <View style={{ flexDirection: "row", gap: 10, height: "100%" }}>
      <View
        style={{
          maxWidth: isMobile ? 644 : 350,
          minWidth: 320,
          flex: 1,
          display: isMobile && selectedConversations !== null ? "none" : "flex",
        }}
      >
        <TitleText
          text="Messages"
          style={{ marginTop: 5, marginBottom: 20 }}
          numberOfLines={1}
        />

        <Tab2
          tabs={["All", "Leads", "Patients"]}
          initialTab="All"
          onTabSwitch={(tab) =>
            setSelectedTab(tab as "All" | "Leads" | "Patients")
          }
        />
        <View style={{ marginVertical: 10 }}>
          <MessageInbox
            isChecked={showUnreadMessagesOnly}
            newMessagesCount={numberOfNewMessages}
            onCheckboxToggle={setShowUnreadMessagesOnly} // Update state directly
          />
        </View>
        <View style={{ flex: 1 }}>
          <ScrollView horizontal={false} showsVerticalScrollIndicator={false}>
            {loading && (
              <ActivityIndicator
                size="large"
                color="#00b7f4"
                style={{ marginTop: 30 }}
              />
            )}
            {filteredConversations.length === 0 && !loading && (
              <Text
                style={{
                  fontSize: 14,
                  fontFamily: "Urbanist400",
                  marginHorizontal: "auto",
                  marginTop: 40,
                }}
              >
                No messages to display
              </Text>
            )}
            {filteredConversations.map((conversation, index) => (
              <MessageItem
                key={index}
                active={conversation.active}
                read={conversation.clinic_read}
                name={conversation.patient_name}
                time={conversation.last_message_at}
                message={conversation.last_message}
                selected={selectedConversations === index}
                conversationType={conversation.conversation_type}
                handleOnPress={() => {
                  setSelectedConversations(index);
                  fetchMessages(conversation.patient_uuid);
                  updateConversationToRead(conversation, index);
                }}
              />
            ))}
          </ScrollView>
        </View>
      </View>
      <View
        style={{
          borderRightWidth: 1,
          borderColor: "#C4C6CD",
          display: isMobile ? "none" : "flex",
        }}
      />
      {selectedConversations !== null && (
        <View style={{ flex: 1, minWidth: 350 }}>
          {messageThread && (
            <MessageThread
              messageThread={messageThread}
              fetchLatestMessageThread={fetchLatestMessageThread}
            />
          )}
        </View>
      )}
    </View>
  );
};

export default HomeMessages;
